import { DURATION_WORDS, describeMinutes, parseDuration } from '../duration.ts';
import { resolveDue, type DueSpec } from '../dueDates.ts';

/**
 * The pure halves of the task forms' shared fields (components/tasks/Fields.tsx), so the tests need no browser.
 * Time spent and due dates are read by the SHARED modules (duration.ts, dueDates.ts, the API's own copies); nothing
 * here parses or resolves for itself.
 */

/** Ruling 10's quick picks, the shared module's own. */
export { QUICK_MINUTES } from '../duration.ts';

/** What the time box says it understood: whole minutes to send and their words, or the parser's own refusal. */
export function timeState(text: string): { minutes: number; echo: string } | { error: string } | { empty: true } {
  if (text.trim() === '') return { empty: true };
  const r = parseDuration(text);
  return 'error' in r ? { error: DURATION_WORDS[r.error] } : { minutes: r.minutes, echo: describeMinutes(r.minutes) };
}

/** Ruling 11's presets, in the order the field offers them, then the counted and the picked. */
export const DUE_PRESETS = [
  ['tomorrow', 'Tomorrow'], ['2_days', 'In 2 days'], ['end_of_week', 'End of week (Friday)'],
  ['after_weekend', 'After the weekend (Monday)'], ['1_week', 'In 1 week'], ['2_weeks', 'In 2 weeks'],
] as const;
export type DueChoice = typeof DUE_PRESETS[number][0] | 'in_days' | 'in_business_days' | 'date' | 'none';

/** The spec a choice sends, or a sentence saying what is missing. N days: 1-365; business days: 1-260. */
export function dueSpecFrom(choice: DueChoice, n: string, date: string, today: string): { spec: DueSpec } | { error: string } {
  if (choice === 'none') return { spec: null };
  if (choice === 'date') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return { error: 'Pick a date.' };
    if (date < today) return { error: 'The due date cannot be before today.' };
    return { spec: { date } };
  }
  if (choice === 'in_days' || choice === 'in_business_days') {
    const max = choice === 'in_days' ? 365 : 260;
    const k = Number(n);
    if (!/^\d+$/.test(n.trim()) || k < 1 || k > max) return { error: `Give a whole number of days, 1 to ${max}.` };
    return { spec: choice === 'in_days' ? { in_days: k } : { in_business_days: k } };
  }
  return { spec: { preset: choice } };
}

/** The date a spec gives, for the field to show before sending; the server resolves it again and is the authority. */
export function duePreview(spec: DueSpec, today: string): string | null {
  const r = resolveDue(spec, today);
  return 'due' in r ? r.due : null;
}

/** "2026-10-23T14:05", the value a datetime-local box takes, for an instant in this browser's own clock. */
export function localValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * "When it happened" (ruling 8): now by default, may be earlier, never in the future. The box holds a local time; the
 * instant sent is ISO 8601. A minute's grace, so "now" set a moment ago is not refused as the future.
 */
export function whenState(value: string, now: Date): { at: string } | { error: string } {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return { error: 'Say when it happened.' };
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return { error: 'Say when it happened.' };
  if (at.getTime() > now.getTime() + 60_000) return { error: 'When it happened cannot be in the future.' };
  return { at: at.toISOString() };
}

const AGENCY_TIME = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

/** An instant (a task's times, a completion) in the agency's own clock, New York: "Oct 23, 2026, 2:05 PM". */
export function formatInstant(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : AGENCY_TIME.format(d);
}
